import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { ShieldCheck, CreditCard, Upload, Ship, CheckCircle, ChevronRight, FileText } from 'lucide-react';
import { useAuth } from '../../contexts/AuthContext';

const PartnerProgram: React.FC = () => {
  const { i18n } = useTranslation();
  const navigate = useNavigate();
  const { isAuthenticated } = useAuth();
  const [step, setStep] = useState(1);
  const [agreed, setAgreed] = useState(false);

  const steps = [
    {
      title: i18n.language.startsWith('zh') ? '1. 註冊夥伴且通過認證' : '1. Register & Verify',
      desc: i18n.language.startsWith('zh') ? '註冊成為夥伴並獲取專屬虛擬採購卡 (VISA)。' : 'Apply to obtain the Incord Virtual Procurement Debit Card (VISA) after verification.',
      icon: <CheckCircle className="text-accent-moss" />
    },
    {
      title: i18n.language.startsWith('zh') ? '2. 獨立採購' : '2. Procure Independently',
      desc: i18n.language.startsWith('zh') ? '使用採購卡於英國線上或實體通路進行消費。' : 'Utilize the card for purchases across UK online and physical retailers.',
      icon: <CreditCard className="text-accent-moss" />
    },
    {
      title: i18n.language.startsWith('zh') ? '3. 上傳憑證' : '3. Upload Invoices',
      desc: i18n.language.startsWith('zh') ? '回傳採購憑證，啟動合規審查與稅務優化。' : 'Submit your purchase receipts via our portal for export compliance and tax optimization review.',
      icon: <Upload className="text-accent-moss" />
    },
    {
      title: i18n.language.startsWith('zh') ? '4. 出貨與返利' : '4. Ship & Rebate',
      desc: i18n.language.startsWith('zh') ? '建立物流訂單，出貨後系統自動派發紅利。' : 'Create personalized logistics orders. Upon successful dispatch, the VAT rebate process is automatically triggered.',
      icon: <Ship className="text-accent-moss" />
    }
  ];

  const handleApply = () => {
    if (!agreed) return;
    if (!isAuthenticated) {
      navigate('/register', { state: { redirectTo: '/settings/partner-program' } });
      return;
    }
    setStep(2);
  };

  return (
    <div className="main-content">
      <div className="container" style={{ maxWidth: '900px', margin: '0 auto' }}>
        <header style={{ marginBottom: '60px', textAlign: 'center' }}>
          <h1 className="page-title" style={{ fontSize: '2.5rem', marginBottom: '16px' }}>
            {i18n.language.startsWith('zh') ? 'Procurement Partner Program (採購夥伴計畫)' : 'Procurement Partner Program'}
          </h1>
          <p style={{ color: 'var(--accent-moss)', fontSize: '1.2rem', fontWeight: 500 }}>
            {i18n.language.startsWith('zh') ? '專為長期自行採購需求夥伴打造的合規基礎設施' : 'The compliance infrastructure for long-term independent procurement partners.'}
          </p>
        </header>

        <div className="section-card" style={{ padding: '40px', marginBottom: '40px' }}>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '30px' }}>
            {steps.map((s, idx) => (
              <div key={idx} style={{ textAlign: 'center' }}>
                <div style={{ 
                  width: '60px', 
                  height: '60px', 
                  borderRadius: '50%', 
                  background: 'rgba(74, 93, 35, 0.1)', 
                  display: 'flex', 
                  alignItems: 'center', 
                  justifyContent: 'center', 
                  margin: '0 auto 20px',
                  color: 'var(--accent-moss)'
                }}>
                  {s.icon}
                </div>
                <h3 style={{ fontSize: '1.1rem', marginBottom: '12px' }}>{s.title}</h3>
                <p style={{ fontSize: '0.9rem', color: '#64748B', lineHeight: 1.5 }}>{s.desc}</p>
              </div>
            ))}
          </div>
        </div>

        <div className="section-card" style={{ padding: '40px' }}>
          <h2 style={{ fontSize: '1.5rem', marginBottom: '24px', display: 'flex', alignItems: 'center', gap: '12px' }}>
            <FileText size={24} color="var(--accent-gold)" />
            {i18n.language.startsWith('zh') ? '📜 Incord B2B 合作夥伴 T&C 核心條款' : 'Incord B2B Partner Core T&Cs'}
          </h2>

          <div style={{ 
            background: 'var(--bg-oatmeal)', 
            padding: '30px', 
            borderRadius: '8px', 
            maxHeight: '400px', 
            overflowY: 'auto',
            border: '1px solid var(--border-light)',
            marginBottom: '30px',
            fontSize: '0.95rem',
            lineHeight: 1.8
          }}>
            <section style={{ marginBottom: '24px' }}>
              <h3 style={{ fontSize: '1.1rem', marginBottom: '12px', color: 'var(--text-charcoal)' }}>
                1. 法律身分界定 (Relationship of Parties)
              </h3>
              <p>明確定義海外買手在點擊同意時，即成為 Lucky Wheel 11 Ltd 的「獨立採購代理 (Independent Procurement Contractor)」。在法律上，您將代表本公司在英國進行商業採購活動。</p>
            </section>

            <section style={{ marginBottom: '24px' }}>
              <h3 style={{ fontSize: '1.1rem', marginBottom: '12px', color: 'var(--text-charcoal)' }}>
                2. 資金與專屬虛擬卡使用限制 (Fund & Card Usage Restrictions)
              </h3>
              <p>夥伴匯入的資金定義為「專案採購準備金 (Sourcing Deposit)」，且發放的虛擬卡<strong>僅限於指定的英國零售商 (Approved UK Retailers) 進行商業採購</strong>。嚴禁提取現金、轉帳或用於個人日常消費。一旦違反使用規定，系統有權立即凍結卡片並處置相關準備金。</p>
            </section>

            <section style={{ marginBottom: '0' }}>
              <h3 style={{ fontSize: '1.1rem', marginBottom: '12px', color: 'var(--text-charcoal)' }}>
                3. 物流出口與所有權強制條款 (Export & Title of Goods)
              </h3>
              <p>透過虛擬卡購買的所有商品，其物理所有權在離開英國國境前，皆歸屬於本公司。<strong>所有商品必須透過 Incord 指定的物流通道 (DHL/FedEx) 出口，絕不允許客戶自行在英國境內取走。</strong> 這是確保符合 HMRC 零稅率出口 (0% VAT) 規範的必要條件。</p>
            </section>
          </div>

          <div style={{ 
            display: 'flex', 
            alignItems: 'center', 
            gap: '12px', 
            marginBottom: '30px',
            padding: '15px',
            background: 'rgba(185, 151, 91, 0.05)',
            borderRadius: '4px',
            border: '1px solid rgba(185, 151, 91, 0.1)'
          }}>
            <input 
              type="checkbox" 
              id="agree-terms" 
              checked={agreed}
              onChange={(e) => setAgreed(e.target.checked)}
              style={{ width: '20px', height: '20px', cursor: 'pointer' }}
            />
            <label htmlFor="agree-terms" style={{ cursor: 'pointer', fontWeight: 500 }}>
              {i18n.language.startsWith('zh') ? '我已閱讀並同意 Incord B2B 合作夥伴核心條款架構' : 'I have read and agree to the Incord B2B Partner Core T&Cs'}
            </label>
          </div>

          <div style={{ display: 'flex', gap: '20px' }}>
            <button 
              className="btn btn-outline" 
              style={{ flex: 1, height: '54px' }}
              onClick={() => navigate('/')}
            >
              {i18n.language.startsWith('zh') ? '返回首頁' : 'Back to Home'}
            </button>
            <button 
              className="btn btn-primary" 
              style={{ flex: 2, height: '54px', fontSize: '1.1rem' }}
              disabled={!agreed}
              onClick={handleApply}
            >
              {i18n.language.startsWith('zh') ? '申請加入計畫' : 'Apply for Program'} <ChevronRight size={20} />
            </button>
          </div>
        </div>

        {step === 2 && (
          <div style={{ 
            position: 'fixed', 
            top: 0, 
            left: 0, 
            right: 0, 
            bottom: 0, 
            background: 'rgba(0,0,0,0.8)', 
            display: 'flex', 
            alignItems: 'center', 
            justifyContent: 'center',
            zIndex: 1000,
            padding: '20px'
          }}>
            <div className="section-card" style={{ maxWidth: '500px', textAlign: 'center', padding: '50px' }}>
              <div style={{ 
                width: '80px', 
                height: '80px', 
                background: 'var(--accent-moss)', 
                color: 'white', 
                borderRadius: '50%', 
                display: 'flex', 
                alignItems: 'center', 
                justifyContent: 'center', 
                margin: '0 auto 30px' 
              }}>
                <CheckCircle size={40} />
              </div>
              <h2 style={{ marginBottom: '16px' }}>{i18n.language.startsWith('zh') ? '請完成身分認證' : 'Complete Your KYC Application'}</h2>
              <p style={{ color: '#64748B', marginBottom: '30px', lineHeight: 1.6 }}>
                {i18n.language.startsWith('zh')
                  ? '請上傳身分證明文件並支付 £13 辦卡費，完成後我們的團隊將在 1-2 個工作日內完成審查並開通您的專屬採購卡。'
                  : 'Upload your ID documents and pay the £13 card fee. Our team will complete KYC review and activate your Partner Visa Card within 1-2 business days.'}
              </p>
              <button className="btn btn-primary" style={{ width: '100%' }} onClick={() => navigate('/settings/partner-program')}>
                {i18n.language.startsWith('zh') ? '前往身分認證' : 'Go to KYC Application'}
              </button>
            </div>
          </div>
        )}

        <div style={{ marginTop: '60px', textAlign: 'center', color: '#64748B' }}>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '8px', marginBottom: '10px' }}>
            <ShieldCheck size={18} />
            <span style={{ fontSize: '0.9rem' }}>Powered by Lucky Wheel 11 Ltd & Compliance First Infrastructure</span>
          </div>
          <p style={{ fontSize: '0.85rem' }}>VISA is a registered trademark of Visa International Service Association.</p>
        </div>
      </div>
    </div>
  );
};

export default PartnerProgram;
